<template>
  <div>
    <Head :title="election.name"/>

    <div class="flex justify-start mb-8">
      <h1 class="text-3xl font-bold">
        <Link class="text-indigo-400 hover:text-indigo-600" :href="route('member.elections.index')">
          {{ $t('models.election') }}
        </Link>
        <span class="text-indigo-400 font-medium mx-2">/</span>
        {{ election.name }}
      </h1>
    </div>

    <trashed-message v-if="election.deleted_at" class="mb-6" @restore="restore">
      {{ $t('alerts.current_model_deleted', [$t('models.election')]) }}
    </trashed-message>

    <div class="bg-white rounded-md shadow overflow-hidden mb-8">
      <div class="flex flex-wrap -mb-8 -mr-6 p-8">
        <text-input v-model="election.name" class="pb-8 pr-6 w-full lg:w-1/3"
                    :label="$t('fields.election.name')" disabled/>
        <text-input v-model="election.datetime_start" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.datetime_start')" disabled/>
        <text-input v-model="election.datetime_end" class="pb-8 pr-6 w-full md:w-1/2 lg:w-1/3"
                    :label="$t('fields.election.datetime_end')" disabled/>
        <textarea-input v-model="election.description" class="pb-8 pr-6 w-full" rows="6"
                        :label="$t('fields.election.description')" disabled/>
      </div>
    </div>

    <h2 class="text-2xl font-bold mb-4">选票</h2>
    <div class="bg-white rounded-md shadow overflow-hidden mb-8">
      <form @submit.prevent="submit">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <div v-if="ballotForm.errors && ballotForm.errors.votes" class="pb-8 pr-6 w-full">
            <div class="form-error">{{ ballotForm.errors.votes }}</div>
          </div>
          <select-input v-for="(vote,key) in ballotForm.votes" :key="key"
                        v-model="ballotForm.votes[key].uuid"
                        :error="ballotForm.errors && ballotForm.errors[`votes.${key}.uuid`]"
                        class="pb-8 pr-6 w-full lg:w-1/2 bg-white"
                        :label="`${$t('models.candidate')} ${(Number(key) + 1)} `"
                        :disabled="!withinTimePeriod || ballot.uuid">
            <option :value="null"/>
            <option v-for="candidate in candidates" :value="candidate.uuid">{{
                [candidate.membership_number, candidate.display_name, candidate.state].filter(x => x).join(' / ')
              }}
            </option>
          </select-input>
        </div>

        <div v-if="ballot.uuid"
             class="flex items-center justify-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          {{ $t('labels.submitted_at') }}
          {{ ballot.created_at }}
        </div>
        <div v-else-if="withinTimePeriod"
             class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-indigo" type="submit">
            {{ $t('actions.save') }}
          </loading-button>
        </div>
      </form>
    </div>

    <h2 class="text-2xl font-bold mb-4">会员名单浏览</h2>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset">
        <label class="block mt-4 text-gray-700">{{ $t('fields.member.state') }}:</label>
        <select v-model="form.state" class="form-select mt-1 w-full">
          <option :value="null"/>
          <option v-for="state in states" :value="state">{{ state }}</option>
        </select>
      </search-filter>
    </div>

    <div v-if="time_to_end > 0" class="whitespace-nowrap">
      <div v-for="candidate in candidates" :key="candidate"
           class="flex justify-start mb-3 bg-white rounded-md shadow overflow-x-auto">
        <img :src="candidate.photo_url" alt="photo" width="100" class="m-1"
             style="min-width: 100px; min-height: 100px;">
        <div class="flex flex-wrap my-2 mr-2 p-2">
          <div class="w-1/2">{{ candidate.full_name_cn }}</div>
          <div class="w-1/2 text-right">{{ candidate.membership_number }}</div>
          <div class="w-full">{{ candidate.full_name }}</div>
          <div class="w-full">{{ candidate.state }}</div>
        </div>
      </div>
    </div>
    <div v-else class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
        <tr class="text-left font-bold">
<!--          <th class="w-px"></th>-->
          <th v-if="time_to_end < 0" class="pb-4 pt-6 px-4">{{
              $t('fields.candidate.votes_count')
            }}
          </th>
          <th class="pb-4 pt-6 px-4">{{ $t('fields.member.full_name_cn') }}</th>
          <th v-if="time_to_end > 0" class="pb-4 pt-6 px-4">{{
              $t('fields.member.membership_number')
            }}
          </th>
          <th class="pb-4 pt-6 px-4">{{ $t('fields.member.state') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="candidate in candidates" :key="candidate.uuid"
            class="hover:bg-gray-100 focus-within:bg-gray-100 cursor-pointer">
<!--          <td class="pl-2 pr-0 py-4 border-t">-->
<!--            <img v-if="candidate.photo_url"-->
<!--                 class="block -my-3 mx-2 h-10 w-auto rounded object-contain"-->
<!--                 style="max-width: inherit;" :src="candidate.photo_url" alt="election cover_image"/>-->
<!--          </td>-->
          <td v-if="time_to_end < 0" class="px-4 py-4 border-t">
            {{ candidate.votes_count }}
          </td>
          <td class="px-4 py-4 border-t">
            {{ candidate.display_name }}
          </td>
          <td v-if="time_to_end > 0" class="px-4 py-4 border-t">
            {{ candidate.membership_number }}
          </td>
          <td class="px-4 py-4 border-t">
            {{ candidate.state }}
          </td>
        </tr>
        <tr v-if="candidates.length === 0">
          <td class="px-4 py-4 border-t" colspan="4">{{ $t('alerts.no_record_found') }}</td>
        </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Layouts/Authenticated'
import TextInput from '@/Inputs/TextInput'
import FileInput from '@/Inputs/FileInput'
import SelectInput from '@/Inputs/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import TextareaInput from "@/Inputs/TextareaInput";
import SearchFilter from "@/Shared/SearchFilter";
import throttle from "lodash/throttle";
import pickBy from "lodash/pickBy";
import mapValues from "lodash/mapValues";
import Icon from "@/Shared/Icon";

export default {
  components: {
    TextareaInput,
    FileInput,
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TrashedMessage,
    SearchFilter,
    Icon,
  },
  layout: Layout,
  props: {
    filters: Object,
    election: Object,
    states: Object,
    candidates: Array,
    ballot: Object,
    time_to_start: Number,
    time_to_end: Number,
  },
  remember: 'form',
  data() {
    return {
      form: {
        search: this.filters.search,
        state: this.filters.state,
      },
      ballotForm: this.$inertia.form({
        votes: {
          0: { uuid: this.ballot.votes[0].uuid },
          1: { uuid: this.ballot.votes[1].uuid },
          2: { uuid: this.ballot.votes[2].uuid },
          3: { uuid: this.ballot.votes[3].uuid },
          4: { uuid: this.ballot.votes[4].uuid },
          5: { uuid: this.ballot.votes[5].uuid },
        },
      }),
    }
  },
  computed: {
    withinTimePeriod() {
      return this.time_to_start < 0 && this.time_to_end > 0;
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(route('member.elections.show', this.election.uuid), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    submit() {
      this.ballotForm.post(route('member.elections.vote', this.election.uuid));
    },
  },
}
</script>
